import React, { useEffect } from 'react';
import { Platform, View, type ViewProps } from 'react-native';
import {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { AnimatedView } from './animated';

export interface FadeInViewProps extends ViewProps {
  delay?: number;
  duration?: number;
  className?: string;
  /**
   * Na web, renderiza o conteúdo já visível, sem entrada animada. O export estático
   * grava a opacidade inicial (0) no HTML, e o texto ficaria invisível para
   * buscador e para o LCP até o JavaScript rodar. Ignorado no nativo.
   */
  disableOnWeb?: boolean;
  children: React.ReactNode;
}

export function FadeInView({
  delay = 0,
  duration = 300,
  className,
  disableOnWeb = false,
  children,
  style,
  ...props
}: FadeInViewProps) {
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(14);

  useEffect(() => {
    const config = { duration, easing: Easing.out(Easing.cubic) };
    opacity.value = withDelay(delay, withTiming(1, config));
    translateY.value = withDelay(delay, withTiming(0, config));
  }, [delay, duration, opacity, translateY]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  if (disableOnWeb && Platform.OS === 'web') {
    return (
      <View className={className} style={style} {...props}>
        <View style={{ flexGrow: 1, flexShrink: 1 }}>{children}</View>
      </View>
    );
  }

  // Classes de layout ficam num View puro: className em animated components é
  // ignorado/clobberado no nativo, o que colapsa flex-1/min-h dos filhos.
  return (
    <View className={className} style={style} {...props}>
      <AnimatedView style={[{ flexGrow: 1, flexShrink: 1 }, animatedStyle]}>
        {children}
      </AnimatedView>
    </View>
  );
}
